import { useWebsiteSettings } from 'client/store'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { LanguageSelector } from 'src/editor/shared/ui-lib/language-selector'
import { getLocalesOptions } from 'src/editor/shared/ui-lib/language-selector/utils/get-locales-options'
import {
  getWebsiteId,
  getWebsiteLocale,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'
import { AddPageLocale } from './add-page-locale'
import { RemovePageLocale } from './remove-page-locale'

interface LocalesSettingsTabProps {
  pageType: PageTypeEnum
  websitePageSetId: number
}

export const LocalesSettingsTab = ({
  pageType,
  websitePageSetId,
}: LocalesSettingsTabProps) => {
  const { t } = useTranslation()

  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const websiteLocale = useWebsiteSettings(getWebsiteLocale)
  const websiteLocales = useWebsiteSettings(getWebsiteLocales)

  const [localeToRemove, setLocaleToRemove] = useState<PageLocaleEnum | null>(
    null,
  )
  const [localeToAdd, setLocaleToAdd] = useState<PageLocaleEnum | null>(null)

  const { data, isFetching } = websitePagesQuery.useQuery({
    queryFnArgs: [websiteId],
  })

  const pageLocales = useMemo(
    () =>
      data?.pages
        .find(page => page.id === websitePageSetId)
        ?.locales.map(locale => locale.locale) ?? [],
    [data?.pages, websitePageSetId],
  )

  const localeOptions = useMemo(() => {
    return getLocalesOptions({
      t,
      localeList: websiteLocales,
      setAdditionalProperties: locale => ({
        nonRemovable: websiteLocale === locale,
      }),
    })
  }, [t, websiteLocales, websiteLocale])

  const handleChangeLocales = (locales: PageLocaleEnum[]) => {
    if (pageLocales.length > locales.length) {
      const localeToRemove = pageLocales.find(
        locale => !locales.includes(locale),
      )
      if (localeToRemove) {
        setLocaleToRemove(localeToRemove)
      }
    } else if (pageLocales.length < locales.length) {
      const localeToAdd = locales.find(locale => !pageLocales.includes(locale))
      if (localeToAdd) {
        setLocaleToAdd(localeToAdd)
      }
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <span className="text-heading-4">{t('page_settings.locales.title')}</span>
      <span className="text-body-M text-textTertiary">
        {t('page_settings.locales.description')}
      </span>
      <LanguageSelector
        isMulti
        menuPortalTarget={document.body}
        isLoading={isFetching || !!localeToAdd}
        isDisabled={
          pageType === PageTypeEnum.WebsiteHome ||
          !!localeToAdd ||
          !!localeToRemove
        }
        selectedLocales={pageLocales}
        options={localeOptions}
        onChange={handleChangeLocales}
      />
      <AddPageLocale
        websitePageSetId={websitePageSetId}
        localeToAdd={localeToAdd}
        onFinallyAddLocale={() => setLocaleToAdd(null)}
      />
      <RemovePageLocale
        websitePageSetId={websitePageSetId}
        localeToRemove={localeToRemove}
        onClose={() => setLocaleToRemove(null)}
      />
    </div>
  )
}
